import React, { useCallback, useEffect, useState } from 'react';
import './Aplazamiento.css';
import { useAuth } from '../../context/AuthContext';
import { useTemporada } from '../../context/TemporadaContext';
import { getAplazamientos } from '../../services/aplazamientoService';
import { getJugadoresTemporadaReal } from '../../services/jugadoresService';
import { formatearFecha } from '../MiZona/estadosPartido';
import InformarPuntos from './InformarPuntos';
import PartidoVs from './PartidoVs';
import Seccion from './Seccion';
import SolicitarAplazamiento from './SolicitarAplazamiento';

const ETIQUETA_POR = { LOCAL: 'el local', VISITANTE: 'el visitante', AMBOS: 'ambas partes', LESION: 'lesión' };

// Pantalla /aplazamiento (lado público, exige sesión): aplazados propios (con
// opción de informar puntos), aplazados por el contrincante y nueva solicitud.
export default function Aplazamiento() {
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const esGestion = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [informando, setInformando] = useState(null);
  // Solo gestión: jugador en cuyo nombre se actúa ('' = el propio, si lo hay).
  const [jugadorElegido, setJugadorElegido] = useState('');
  const [jugadores, setJugadores] = useState([]);

  const cargar = useCallback(() =>
    getAplazamientos(temporada, esGestion ? jugadorElegido : '')
      .then((d) => { setDatos(d); setError(null); })
      .catch((err) => setError(err)), [temporada, esGestion, jugadorElegido]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!esGestion || !temporada) return;
    getJugadoresTemporadaReal(temporada)
      .then((lista) => setJugadores([...lista].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(() => setJugadores([]));
  }, [esGestion, temporada]);

  const cambiarJugador = (valor) => {
    setJugadorElegido(valor);
    setInformando(null);
  };

  if (error) {
    return (
      <p className="msg-error mz-estado-pagina" role="alert">
        {error.codigo === 'SIN_JUGADOR'
          ? 'Tu usuario no está vinculado a ningún jugador, así que no puedes solicitar aplazamientos.'
          : error.estado === 401
            ? 'Inicia sesión para gestionar tus aplazamientos.'
            : error.message || 'No se pudo cargar la pantalla de aplazamientos.'}
      </p>
    );
  }
  if (!datos) return <p className="mz-estado-pagina">Cargando…</p>;

  return (
    <div className="aplazamiento page-content">
      <header className="page-header">
        <h2>Aplazamiento</h2>
        <span className="chip chip-muted">{datos.temporada.nombre}</span>
      </header>

      {esGestion && (
        <div className="ap-campo ap-gestion">
          <label htmlFor="ap-jugador">Aplazar en nombre de</label>
          <select id="ap-jugador" value={jugadorElegido} onChange={(e) => cambiarJugador(e.target.value)}>
            <option value="">{usuario.jugadorId ? '— yo mismo —' : '— elige un jugador —'}</option>
            {jugadores.map((j) => <option key={j.id} value={j.id}>{j.nombre}{j.club ? ` (${j.club})` : ''}</option>)}
          </select>
        </div>
      )}

      {datos.sinJugador && !jugadorElegido && (
        <p className="ap-banner" role="note">
          Tu usuario no está vinculado a ningún jugador: ves la pantalla en modo consulta. Elige un jugador para registrar un aplazamiento en su nombre.
        </p>
      )}

      {(datos.enRevision ?? []).length > 0 && (
        <Seccion titulo="Pendientes de revisión del comité" total={datos.enRevision.length}>
          <ul className="ap-lista">
            {datos.enRevision.map((p) => (
              <li key={p.partidoId} className="ap-fila">
                <span>Jornada {p.jornada} · {p.modalidad} · <PartidoVs partido={p} /></span>
                <span className="ap-limite">El comité revisará tu justificante. Hasta entonces el partido sigue pendiente.</span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      <Seccion titulo="Partidos aplazados solicitados por ti" total={datos.propios.length}>
        {datos.propios.length === 0 ? <p className="mz-vacio">No tienes partidos aplazados.</p> : (
          <ul className="ap-lista">
            {datos.propios.map((p) => (
              <li key={p.partidoId}>
                <div className="ap-fila">
                  <span>Jornada {p.jornada} · {p.modalidad} · <PartidoVs partido={p} /></span>
                  {p.justificado ? (
                    <span className="ap-limite">
                      Justificante aprobado por el comité · {p.fechaJuego ? `nueva fecha ${formatearFecha(p.fechaJuego)}` : 'nueva fecha por fijar'}
                    </span>
                  ) : (
                    <span className={p.vencido ? 'msg-error' : 'ap-limite'}>
                      {p.vencido ? 'Plazo vencido el' : 'Recupéralo antes del'} {formatearFecha(p.fechaLimite)}
                    </span>
                  )}
                  {!p.vencido && (
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => setInformando((a) => (a === p.partidoId ? null : p.partidoId))}>
                      {informando === p.partidoId ? 'Cancelar' : 'Informar puntos'}
                    </button>
                  )}
                </div>
                {informando === p.partidoId && (
                  <InformarPuntos partido={p} jugadorId={jugadorElegido} onGuardado={() => { setInformando(null); cargar(); }} />
                )}
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      <Seccion titulo="Partidos aplazados por el contrincante" total={datos.contrincante.length}>
        {datos.contrincante.length === 0 ? <p className="mz-vacio">Ningún contrincante ha aplazado partido contra ti.</p> : (
          <ul className="ap-lista">
            {datos.contrincante.map((p) => (
              <li key={p.partidoId} className="ap-fila">
                <span>Jornada {p.jornada} · {p.modalidad} · <PartidoVs partido={p} /></span>
                <span className="ap-limite">
                  Aplazado por {ETIQUETA_POR[p.aplazadoPor] ?? 'otro motivo'}
                  {p.justificado
                    ? ` con justificante aprobado · ${p.fechaJuego ? `nueva fecha ${formatearFecha(p.fechaJuego)}` : 'nueva fecha por fijar'}`
                    : ` · fecha límite ${formatearFecha(p.fechaLimite)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      <Seccion titulo="Solicitar nuevo aplazamiento">
        <SolicitarAplazamiento key={jugadorElegido} solicitables={datos.solicitables} plazoDias={datos.plazoDias} jugadorId={jugadorElegido} onSolicitado={cargar} />
      </Seccion>
    </div>
  );
}
